import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Paper,
  IconButton,
  Collapse,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import ChannelStepsEditor from './ChannelStepsEditor';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import { useEscalationChainTab } from '../hooks/useEscalationChainTab';
import type { ContactWithUser, ChannelStep } from '../../../types';

interface EscalationChainTabContentProps {
  isActive: boolean;
  customerId: string;
  contacts: ContactWithUser[];
}

const EscalationChainTabContent: React.FC<EscalationChainTabContentProps> = ({
  isActive,
  customerId,
  contacts,
}) => {
  const { state, dispatch, loading, saving, snackbar, closeSnackbar, handleSave, getContactName, hasCloud2 } =
    useEscalationChainTab(isActive, customerId, contacts);

  const handleToggleExpand = useCallback(
    (index: number) => {
      dispatch({ type: 'SET_EXPANDED', payload: state.expandedLevel === index ? null : index });
    },
    [dispatch, state.expandedLevel],
  );

  const handleChannelStepsChange = useCallback(
    (levelIndex: number, steps: ChannelStep[]) => {
      dispatch({ type: 'SET_CHANNEL_STEPS', payload: { levelIndex, steps } });
    },
    [dispatch],
  );

  const cloud2Count = state.levels.filter((l) => l.level_type === 'cloud2').length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {state.levels.map((level, index) => {
          const isCloud2 = level.level_type === 'cloud2';
          const isExpanded = state.expandedLevel === index;
          const isLastCloud2 = isCloud2 && cloud2Count <= 1;

          return (
            <Paper
              key={index}
              variant="outlined"
              sx={{
                mb: 1.5,
                overflow: 'hidden',
                bgcolor: isCloud2 ? 'action.hover' : 'background.paper',
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, cursor: 'pointer' }}
                onClick={() => handleToggleExpand(index)}
              >
                <Chip
                  label={`${index}`}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 'bold', minWidth: 36 }}
                />

                <Chip
                  label={isCloud2 ? 'Cloud2' : 'Customer'}
                  size="small"
                  variant={isCloud2 ? 'filled' : 'outlined'}
                  color={isCloud2 ? 'primary' : 'secondary'}
                  sx={{ fontSize: (theme) => theme.typography.caption.fontSize }}
                />

                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    {level.name || '(unnamed step)'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {level.description}
                  </Typography>
                </Box>

                {!isCloud2 && level.contact_username && (
                  <Chip
                    label={getContactName(level.contact_username)}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: (theme) => theme.typography.caption.fontSize }}
                  />
                )}

                {level.channel_steps.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {level.channel_steps.length} step{level.channel_steps.length !== 1 ? 's' : ''}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch({ type: 'REORDER_LEVELS', payload: { fromIndex: index, toIndex: index - 1 } });
                    }}
                    disabled={index <= 0}
                  >
                    <ArrowUpIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch({ type: 'REORDER_LEVELS', payload: { fromIndex: index, toIndex: index + 1 } });
                    }}
                    disabled={index >= state.levels.length - 1}
                  >
                    <ArrowDownIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleExpand(index);
                    }}
                  >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              </Box>

              <Collapse in={isExpanded}>
                <Box sx={{ px: 2, pb: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      size="small"
                      label="Step Name"
                      value={level.name}
                      onChange={(e) =>
                        dispatch({ type: 'UPDATE_LEVEL', payload: { index, updates: { name: e.target.value } } })
                      }
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      label="Description"
                      value={level.description}
                      onChange={(e) =>
                        dispatch({ type: 'UPDATE_LEVEL', payload: { index, updates: { description: e.target.value } } })
                      }
                      sx={{ flex: 2 }}
                    />
                  </Box>

                  {!isCloud2 && (
                    <Autocomplete
                      size="small"
                      sx={{ mb: 2, maxWidth: 360 }}
                      options={contacts}
                      getOptionLabel={(c) => c.name || c.email}
                      value={contacts.find((c) => c.username === level.contact_username) ?? null}
                      onChange={(_, newValue) =>
                        dispatch({
                          type: 'UPDATE_LEVEL',
                          payload: { index, updates: { contact_username: newValue?.username ?? null } },
                        })
                      }
                      renderInput={(params) => <TextField {...params} label="Primary Contact" />}
                    />
                  )}

                  <ChannelStepsEditor
                    steps={level.channel_steps}
                    availableChannels={state.availableChannels}
                    contacts={contacts}
                    onChange={(steps) => handleChannelStepsChange(index, steps)}
                  />

                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => dispatch({ type: 'REMOVE_LEVEL', payload: index })}
                      disabled={isLastCloud2}
                      title={isLastCloud2 ? 'A chain must keep at least one Cloud2 step' : undefined}
                    >
                      Remove Step
                    </Button>
                  </Box>
                </Box>
              </Collapse>
            </Paper>
          );
        })}

        <Box sx={{ mt: 1, display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => dispatch({ type: 'ADD_LEVEL', payload: { levelType: 'customer' } })}
          >
            Add Customer Step
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => dispatch({ type: 'ADD_LEVEL', payload: { levelType: 'cloud2' } })}
          >
            Add Cloud2 Step
          </Button>
        </Box>
      </Box>

      <Box sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
        {!hasCloud2 && (
          <Typography variant="caption" color="error">
            A chain must contain at least one Cloud2 step.
          </Typography>
        )}
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || !hasCloud2}
        >
          {saving ? 'Saving...' : 'Save Chain'}
        </Button>
      </Box>

      <SnackbarAlert
        snackbar={snackbar}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default EscalationChainTabContent;
